const $ = require('jquery');
const log = require('core/src/log').instance("client/ui/container");
const EventInterface = require('core/src/event-interface');
const _ = require('core/src/utils/legacy');
const { isjQuery } = require('client/src/utils/jquery');
const { checkMethods, checkType } = require('utils/src/validation');
const VueView = require('./vue-view').default;
const ViewRenderer = require('./view-renderer');
const onDOMElementsResized = require('client/src/utils/onDOMElementsResized');
const ScopedPolyfill = require('client/libraries/style-scoped-polyfill');
const { isTrue } = require('core/src/utils/validation');
const { ACTION_TRIGGER_TYPE, getMenuConfig } = require('client/src/utils/container-menu');

/**
 * A container to display View renders
 * @param {string} id               The container ID.
 * @param {object} containerData
 * @returns {ViewContainer}
 */
const ViewContainer = function (id, containerData) {
	containerData = _.ensure(containerData, _.isObject, {});
	this.id = id;
	this.classes = ["graphileon-container"];
	this._listeners = {
		resize: [],
		ready: [],
		close: []
	};

	var classes = undefined;
	if (_.isString(containerData.classes)) {
		classes = containerData.classes.split(' ');
	} else if (_.isArray(containerData.classes)) {
		classes = containerData.classes;
	}
	if (classes !== undefined) {
		this.classes = this.classes.concat(classes);
	}

	this.element = this.build(containerData);
	this.$element = this.element;
	if(!isjQuery(this.$element)) this.$element = $(this.element);

	this.addCustomCSS(containerData.css);

	this.$element.one('remove', () => {
		// If container is suddenly removed from the DOM, assume the user is the cause
		this.close('user');
	});

	this._config = {};

	this._eventInterface = new EventInterface();
	this._eventInterface.extend(this);
	this.childrenContainerNames = []; // IDs of (view) containers that are DOM children of this container
};

ViewContainer.State = {
	MAXIMIZED: 'maximized',
	MINIMIZED: 'minimized',
	NORMALIZED: 'normalized',
	COLLAPSED: 'collapsed'
};

ViewContainer.prototype._state = ViewContainer.State.NORMALIZED;
ViewContainer.prototype._config = undefined;
ViewContainer.prototype._zPosition = undefined;
ViewContainer.prototype._listeners = undefined;
ViewContainer.prototype._renderer = undefined;
ViewContainer.prototype._menu = undefined; // open floating menu (ViewRenderer.ContextMenu), if any
ViewContainer.prototype._onRendererClose = function () {
};


ViewContainer.prototype.id = undefined;
ViewContainer.prototype.content = undefined;
ViewContainer.prototype.element = undefined;
ViewContainer.prototype.$element = undefined; // jQuery version of same element
ViewContainer.prototype.classes = undefined;
ViewContainer.prototype.properties = undefined;
ViewContainer.prototype.index = undefined;
ViewContainer.prototype.isNew = undefined; // current content is the first content of this container


ViewContainer.ACTION_TRIGGER_TYPE = ACTION_TRIGGER_TYPE;

/**
 * Reads the optional menu configuration from the container data.
 * @param {object} containerData    Anything set on $container in the View.
 * @returns {object|undefined}      {mode, action, options, label, iconClass}, or undefined if not configured.
 */
ViewContainer.prototype.getMenuConfig = function (containerData) {
	return getMenuConfig(containerData);
};

/**
 * Fires a 'container-action' trigger for the given action. Used by both operating modes:
 * the argument is either the whole config (action mode) or a single option (menu mode).
 * @param {object} action    Anything exposing an `action` string.
 */
ViewContainer.prototype.fireAction = function (action) {
	if (!action) return;
	if (!this._renderer) {
		log.warn("No renderer attached to container. Cannot fire container action.");
		return;
	}
	this._renderer.trigger({
		type: ViewContainer.ACTION_TRIGGER_TYPE,
		action: action.action
	});
};

/**
 * Builds the header button element.
 * @param {object} config    As returned by getMenuConfig.
 * @returns {jQuery}
 */
ViewContainer.prototype.buildMenuButton = function (config) {
	// d-inline-flex + text-nowrap keep the icon and label on one line; flex-shrink-0 stops the
	// header's flexible title area (w-100) from squeezing the button until its content wraps.
	const $button = $('<button type="button" class="container-menu-button btn btn-sm border-0 py-0 me-1 d-inline-flex align-items-center text-nowrap flex-shrink-0" data-action="container-action">');

	if (config.iconClass) {
		const $icon = $('<i>').addClass(config.iconClass);
		if (config.label.length > 0) {
			$icon.addClass('me-2');
		}
		$button.append($icon);
	}
	$button.append(document.createTextNode(config.label));
	this.applyTooltip($button, config.tooltip);
	$button.on('click', event => {
		// Keep the click from reaching container-level handlers (e.g. jsPanel header, modal background)
		event.stopPropagation();
		if (config.mode === 'menu') {
			this.openMenu(config, event.currentTarget);
		} else {
			this.fireAction(config);
		}
	});
	// jsPanel starts dragging on pointerdown in the header; don't let it treat this button as a handle
	$button.on('mousedown touchstart pointerdown', event => event.stopPropagation());

	return $button;
};

/**
 * Opens the floating menu below the given button.
 *
 * Built on ViewRenderer.ContextMenu, which appends to <body> with absolute positioning: that
 * keeps the menu clear of any ancestor `overflow` (notably the jsPanel header), lets one
 * implementation serve every container, and reuses the app-wide open/close coordination.
 * Rebuilt on each click so it always reflects the current configuration.
 *
 * @param {object} config          As returned by getMenuConfig, in 'menu' mode.
 * @param {HTMLElement} buttonEl   The button to position the menu against.
 */
ViewContainer.prototype.openMenu = function (config, buttonEl) {
	this.destroyMenu();

	const menu = new ViewRenderer.ContextMenu('container-menu-' + this.id);
	_.forEach(config.options, (option, index) => {
		// Items are keyed by [group][label], so equal labels would overwrite each other. Using the
		// array index as the group makes every key unique and, via _.compareIndex, preserves order.
		const item = menu.addItem(index, this.buildMenuItemLabel(option), () => this.fireAction(option));

		// On the <li> rather than inside the label, so the whole row is a hover target.
		// ContextMenu.Item renders its label with .html(), which would strip an attribute anyway.
		// Placed to the right: above/below would cover the neighbouring items.
		if (item) {
			this.applyTooltip(item.element, option.tooltip, 'right');
		}
	});
	menu.render();
	menu.element.addClass('container-menu');
	this._menu = menu;

	const rect = buttonEl.getBoundingClientRect();
	// The menu is body-appended and absolutely positioned, so it needs page (not viewport) coordinates.
	menu.open(null, rect.left + window.scrollX, rect.bottom + window.scrollY);

	// Right-align to the button: header buttons sit at the right edge, so a menu wider than its
	// button would otherwise run off-screen. Measurable only once the menu is in the document.
	const width = menu.element.outerWidth();
	menu.element.css('left', Math.max(0, rect.right + window.scrollX - width) + 'px');
};

/**
 * Builds the inner markup for one menu item.
 * ContextMenu.Item renders its label with .html(), so an icon needs no change to that class.
 * @param {object} option    As returned by getMenuConfig, one entry of `options`.
 * @returns {string}
 */
ViewContainer.prototype.buildMenuItemLabel = function (option) {
	const label = _.escape(option.label);
	if (!option.iconClass) {
		return label;
	}
	const icon = `<i class="${_.escape(option.iconClass)}"></i>`;
	return label.length > 0 ? `${icon} ${label}` : icon;
};

/**
 * Adds a tooltip to an element, following the convention used elsewhere in the app.
 *
 * `data-bs-toggle` lets the app's tooltip initialiser pick the element up and render a styled
 * Bootstrap tooltip; where that initialiser is absent the browser falls back to `title` and shows
 * the same text natively, so no explicit instance is created (or has to be disposed of) here.
 *
 * @param {jQuery} $element
 * @param {string} [tooltip]    No-op when empty.
 * @param {string} [placement]
 */
ViewContainer.prototype.applyTooltip = function ($element, tooltip, placement = 'top') {
	if (!_.isString(tooltip) || tooltip.length === 0) return;

	$element.attr({
		title: tooltip,
		'data-bs-toggle': 'tooltip',
		'data-bs-placement': placement
	});
};

/**
 * Removes the floating menu from the document, if one was opened.
 */
ViewContainer.prototype.destroyMenu = function () {
	if (!this._menu) return;

	this._menu.close(true); // `permanent`: remove rather than detach, so nothing is left in <body>
	this._menu = null;
};

/**
 * Builds an empty container header: the title area and the area holding the header buttons.
 * @param {string} [headerClass]    Extra classes from $container.headerClass
 * @returns {jQuery}
 */
ViewContainer.prototype._buildHeader = function (headerClass) {
	const headerEl = $(`<div class="container-header card-header d-flex border-0 ${_.isString(headerClass) ? headerClass : ''}">`);

	headerEl.append($(`
		<div class="container-title-area w-100 text-secondary me-4">
			<label class="title"></label>
		</div>`));
	headerEl.append($('<div class="container-header-buttons d-flex align-items-center flex-shrink-0">'));

	return headerEl;
};

/**
 * Builds the container element. Override for specific containers.
 * @param {string} containerID
 * @param {object} containerData
 * @param {string[]} classes
 * @returns {jQuery} The container element, as a jQuery object
 */
ViewContainer.prototype._build = function (containerID, containerData, classes) {
	var self = this;
	containerData = _.ensure(containerData, _.isObject, {});

	const title = containerData['title'];
	const collapsable = containerData['collapsable'];
	const closable = containerData['closable'];
	const headerVisible = containerData['headerVisible'];
	const headerClass = containerData['headerClass'];
	const borderVisible = containerData['borderVisible'] || false;
	const menuConfig = this.getMenuConfig(containerData);

	let header = false;
	if (!_.isEmpty(_.trim(title, ' ')) || collapsable || closable || menuConfig) {
		header = true;
	}
	if (!_.isNil(headerVisible) && !headerVisible) {
		header = false;
	}

	let borderClass = 'border-0';
	if (borderVisible) {
		borderClass = '';
	}

	// CONTAINER
	var containerdiv = $(`<div class="default card ${borderClass}">`);

	// HEADER
	if (header) {
		const headerEl = this._buildHeader(headerClass);
		const actionButtons = headerEl.find('.container-header-buttons').first();

		// Configurable button ($container.menu), left of the collapse/close controls
		if (menuConfig) {
			headerEl.addClass('has-container-menu');
			actionButtons.append(this.buildMenuButton(menuConfig));
		}

		// Collapse button
		if (containerData['collapsable']) {
			containerdiv.addClass('collapsable-container');

			if (containerData['collapsed']) {
				containerdiv.addClass('collapsed');
			}

			let collapseButton = $(`
				<div class="me-1">
					<i class="fa clickable collapse-container-button d-flex align-items-center" data-action="switch-collapsable"></i>
				</div>`);
			actionButtons.append(collapseButton);

			collapseButton.click(() => {
				const state = containerdiv.hasClass('collapsed') ? ViewContainer.State.NORMALIZED : ViewContainer.State.COLLAPSED;
				this.updateState('state', state);
			});
		}

		// Close button
		if (containerData['closable']) {
			actionButtons.append(`
				<div>
					<i class="fa fa-times clickable d-flex align-items-center" data-action="close-container"></i>
				</div>`);
			actionButtons.on('click', '[data-action="close-container"]', function () {
				self.close('user');
			});
		}

		containerdiv.append(headerEl);
	}

	// CONTENT
	var contentWrapper = $('<div class="container-body card-body">');
	var content = $('<div class="container-content h-100 w-100">');
	contentWrapper.append(content);
	// Listen to resize events
	this.contentResizeObserver = onDOMElementsResized(content, () => self.resize());
	containerdiv.append(contentWrapper);

	containerdiv.attr('id', containerID);
	for (var i in classes) {
		containerdiv.addClass(classes[i]);
	}

	return containerdiv;
};

/**
 * Checks whether the container is still available, or removed. Override for specific containers.
 * @returns {boolean}
 */
ViewContainer.prototype._isAvailable = function () {
	var find = document.getElementById(this.id);
	return find !== null;
};

/**
 * Attaches the container to an area. Override for specific containers.
 * @param {jQuery} container    The container
 * @param {jQuery} area         The area to attach to, as a jQuery object.
 * @param {jQuery} [before]     [optional] The element to place the container before.
 */
ViewContainer.prototype._attachTo = function (container, area, before) {
	if (before !== undefined) {
		before.before($(container));
	} else {
		area.append($(container));
	}
};

ViewContainer.prototype._setTitle = function (element, title) {
	var $title = $(element).find('.title');
	$title.html(title);
	if (_.def(title)) {
		$title.show();
	} else {
		$title.hide();
	}
};
/**
 * Renders (or removes) the header button. Override for specific containers.
 * @param {jQuery} element
 * @param {object} [config]           As returned by getMenuConfig. Removes the button if undefined.
 * @param {object} [containerData]    The container config the menu was read from.
 */
ViewContainer.prototype._setMenu = function (element, config, containerData) {
	let $header = $(element).find('.container-header').first();

	if ($header.length === 0) {
		// The header is only built when something needs to go in it, so a container created without
		// title, collapse and close controls has none at all. Build it now instead of silently
		// dropping a menu that is configured after the container was built.
		if (! config) return;
		if (_.get(containerData, 'headerVisible') === false) return;

		$header = this._buildHeader(_.get(containerData, 'headerClass'));
		$(element).prepend($header);
	}

	$header.find('.container-menu-button').remove();
	$header.toggleClass('has-container-menu', !! config);
	if (config) {
		$header.find('.container-header-buttons').first().prepend(this.buildMenuButton(config));
	}
};
ViewContainer.prototype._close = function (element) {
	$(element).remove();
	if (this.contentResizeObserver) {
		this.contentResizeObserver.disconnect();
	}
};
ViewContainer.prototype._setSize = function (element, width, height) {
	if (! _.isNil(width)) {
		$(element).css('width', (width >= 0 ? width + "px" : width));
	}

	if (! _.isNil(height)) {
		$(element).css('height', (height >= 0 ? height + "px" : height));
	}
};
ViewContainer.prototype._setAttribute = function (element, attribute, value) {
	$(element).attr(attribute, value);
};
ViewContainer.prototype._getInnerSize = function (element) {
	var content = $(element).find('.container-content');
	return {
		width: content.width(),
		height: content.height()
	};
};
ViewContainer.prototype._setState = function (element, state) {
	if(state === ViewContainer.State.COLLAPSED) {
		$(element).addClass('collapsed');
	} else {
		$(element).removeClass('collapsed');
	}
};
ViewContainer.prototype._setPosition = function (element, zPosition) {
	if (zPosition === 'front') {
		this._moveToFront(element);
	} else if (zPosition === 'back') {
		this._moveToBack(element);
	}
};
ViewContainer.prototype._focus = function (element) {
	// No default implementation
};

ViewContainer.prototype._moveToFront = function (element) {
	// No default implementation
};


ViewContainer.prototype._moveToBack = function (element) {
	// No default implementation
};


ViewContainer.prototype._setOriginFunctionCallback = function (cb) {
	// No default implementation
};


/**
 * Get the unique ID of this container.
 * @returns {undefined}
 */
ViewContainer.prototype.getId = function () {
	return this.id;
};

/**
 * Checks whether the container is still available on the page, or has been removed.
 * @returns {boolean}
 */
ViewContainer.prototype.isAvailable = function () {
	return this._isAvailable();
};

/**
 * Builds the container.
 * @param {object} containerData The container identifier.
 * @returns {jQuery}
 */
ViewContainer.prototype.build = function (containerData) {
	const built = this._build(this.id, containerData, this.classes);
	this.setConfig(containerData);

	if (!isjQuery(built) && !(built instanceof HTMLElement)) {
		log.error("Custom container build function did not return a jQuery object or HTMLElement");
		return $('');
	}
	return built;
};

ViewContainer.prototype.addCustomCSS = function(css) {
	if (! _.isString(css) || ! (css.length > 0)) {
		return;
	}

	var style = $('<style type="text/css">').text(css);
	this.$element.prepend(style);
	var scope = ScopedPolyfill.scopeStyle(style[0]);
	this.$element.addClass(scope);
}

/**
 * Attaches the container to the given area.
 * @param {jQuery} area The area as a jQuery object
 */
ViewContainer.prototype.attachTo = function (area) {
	if (!isjQuery(area)) {
		log.error("Area parameter is not a jQuery object.");
		return false;
	}

	var index = this.index ? this.index : 0;
	var before = undefined;
	var valid = _.validate({
		index: [index, _.isStringOrNumber(index), "Must be string or number", {
			default: 0,
			warn: index !== undefined
		}]
	}, "Will not use index.").isValid();
	if (valid !== false) {
		var indexes = _.getFromjQueryCollection(area.children(), 'attr', ['data-index']);
		for (var i in indexes) {
			if (indexes[i] === undefined) {
				indexes[i] = 0;
			}
		}
		indexes.push(index);
		indexes.sort(_.compareIndex);
		before = area.children().eq(_.findLastIndex(indexes, i => i === index));
		if (before.length === 0) {
			before = undefined;
		}
	}

	this._attachTo(this.element, area, before);

	return true;
};

/**
 * Sets the title of the container.
 * @param {string} title
 * @returns {undefined}
 */
ViewContainer.prototype.setTitle = function (title) {
	this._setTitle(this.element, title);
};

/**
 * Sets the header button ($container.menu) of the container.
 * @param {object} containerData    Anything set on $container in the View.
 */
ViewContainer.prototype.setMenu = function (containerData) {
	// A rebuilt button invalidates any menu opened from the previous one.
	this.destroyMenu();
	// containerData is passed on because update() runs setMenu before setConfig, so this.config
	// still holds the previous values at this point.
	this._setMenu(this.element, this.getMenuConfig(containerData), containerData);
};

/**
 * Set the order index of the container
 * @param [string|Number] index
 */
ViewContainer.prototype.setIndex = function (index) {
	this.index = index;
	if (index === undefined) {
		this.$element.removeAttr('data-index');
	} else {
		this.$element.attr('data-index', index);
	}
};

/**
 * Sets the (preferred) size of the container
 * @param {number} width
 * @param {number} height
 */
ViewContainer.prototype.setSize = function (width, height) {
	if(this._setSize(this.element, width, height) !== false) {
		this.resize(); // notify listeners
	}
	if (_.def(height)) {
		this.$element.addClass('container-fixed-height');
		this.$element.removeClass('container-content-height');
	} else {
		this.$element.removeClass('container-fixed-height');
		this.$element.addClass('container-content-height');
	}
};

/**
 * Removes the container without notifying anyone.
 */
ViewContainer.prototype.destroy = function() {
	// The floating menu lives in <body>, not in the container, so it must be removed explicitly.
	this.destroyMenu();
	this._close(this.element);
};

/**
 * Closes the container.
 * @param {string} origin    The origin of the close event.
 * @returns {undefined}
 */
ViewContainer.prototype.close = function (origin) {
	if (this._isClosed) {
		return
	}

	this._closeRenderer(origin);
	this.fire('close', origin);
	this.destroy();

	this._isClosed = true;
};

/**
 * Removes all content from the Container and detaches the renderer.
 * @param {string} origin    The origin of the close event.
 */
ViewContainer.prototype.empty = function (origin) {
	this.setTitle('Empty');

	this._closeRenderer(origin);
	this.setContent('');
};

ViewContainer.prototype.setAttribute = function (attribute, value) {
	this._setAttribute(this.element, attribute, value);
};

ViewContainer.prototype.getInnerSize = function () {
	var innerSize = this._getInnerSize(this.element);
	var width = _.get(innerSize, 'width');
	var height = _.get(innerSize, 'height');

	var valid = _.validate('getInnerSize', {
		height: [height, 'isNumber'],
		width: [width, 'isNumber']
	}, "Invalid size returned by Container's getInnerSize.").isValid();

	if (! valid) return {height: 0, width: 0};

	return {width, height};
};

/**
 * Set the content of this container.
 * @param {jQuery|string} content   Anything that can be used as html.
 * @param {ViewRenderer} [renderer]     [optional] If set, the renderer will be connected to important events, such as close, resize, etc.
 * @returns {jQuery|boolean}
 */
ViewContainer.prototype.setContent = function (content, renderer) {
	this.content = content;

	if(renderer) {
		this.attachRenderer(renderer);
	}

	if(isjQuery(content)) content = content[0];
	return this._setContent(this.element, content);
};
/**
 * Sets the content for the container. Override for specific containers.
 * @param {jQuery} element          The container element, as a jQuery object.
 * @param {jQuery|string} content   Anything that can be used as html.
 */
ViewContainer.prototype._setContent = function (element, content) {
	// HTML view can have .contaner-content inside so we need only the parent one
	$(element).find('.container-content').first().html(content);
};

ViewContainer.prototype.getContent = function() {
	return this.content;
};

ViewContainer.prototype.setOriginFunctionCallback = function (callback) {
	this._setOriginFunctionCallback(this.element, callback);
};

ViewContainer.prototype._setOriginFunctionCallback = function (element, callback) {
	let button = $('<div class="graphileon-origin-function-button fa fa-cog">');
	button.on('click', callback);
	$(element).append(button[0]);
};

ViewContainer.prototype.detachRenderer = function () {
	var renderer = this._renderer;
	this._detachRenderer();
	return renderer;
};

ViewContainer.prototype.checkRenderer = function(renderer) {
	checkMethods(renderer, [
		'on', 'close', 'removeListener', 'resize', 'ready', 'getFunctionID', 'getInstanceID'
	], 'renderer');
};

ViewContainer.prototype.attachRenderer = function (renderer) {
	this.checkRenderer(renderer);

	if (this._renderer) {
		log.error("A renderer was already attached to this container.");
		return false;
	}

	if (! (renderer)) {
		log.error('ViewContainer.attachRenderer: Expecting ViewRenderer instance as parameter');
		return false;
	}

	this._onRendererClose = origin => this.close(origin);
	this._onComponentClose = origin => this.close(origin);
	this._onComponentContainerChange = specs => this.update(specs);

	renderer.on('Close', this._onRendererClose, true);
	renderer.on(VueView.Event.Out.COMPONENT_CLOSED, this._onComponentClose);
	renderer.on(VueView.Event.Out.CONTAINER_CHANGED, this._onComponentContainerChange);

	this._renderer = renderer;
};

/**
 *
 * @param {string} origin    The origin of the close event
 * @private
 */
ViewContainer.prototype._closeRenderer = function (origin) {
	if (this._renderer) {
		var renderer = this._renderer;
		this._detachRenderer();
		renderer.close(origin);
	}
};

ViewContainer.prototype._detachRenderer = function () {
	if (this._renderer) {
		this._renderer.removeListener('Close', this._onRendererClose);
		this._renderer.removeListener(VueView.Event.Out.COMPONENT_CLOSED, this._onComponentClose);
		this._renderer.removeListener(VueView.Event.Out.CONTAINER_CHANGED, this._onComponentContainerChange);
		this._renderer = undefined;
	}
};

/**
 * Notify all listeners that the container has been resized.
 * @returns {undefined}
 */
ViewContainer.prototype.resize = function () {
	var innerSize = this.getInnerSize();
	var width = innerSize.width;
	var height = innerSize.height;

	if (! (width >= 0 && height >= 0)) {
		return;
	}

	this.fire('resize', {width: width, height: height});

	if (this._renderer instanceof ViewRenderer) {
		this._renderer.resize(width, height);
	}
};

/**
 * Notify all listeners that the container is ready.
 */
ViewContainer.prototype.ready = function () {
	this.fire('ready');
	if(this._renderer) {
		this._renderer.ready();
	}
};

/**
 * Get the ID of the Function that currently has an instance represented in this container.
 * @returns {*}
 */
ViewContainer.prototype.getCurrentFunctionID = function () {
	if (this._renderer instanceof ViewRenderer) {
		return this._renderer.getFunctionID();
	} else {
		return null;
	}
};

/**
 * Get the ID of the instance that is currently represented in the container.
 * @returns {*}
 */
ViewContainer.prototype.getCurrentInstanceID = function () {
	if (this._renderer instanceof ViewRenderer) {
		return this._renderer.getInstanceID();
	} else {
		return null;
	}
};

ViewContainer.prototype.focus = function () {
	this._focus(this.element);
};

/**
 * Set the container state.
 * @param {string} state    See ViewContainer.State for options
 * @param {boolean} force    Force state change even if it's the same state.
 * @returns {boolean}        True if the state changed, false if not (no call will be made to container's _setState).
 */
ViewContainer.prototype.setState = function (state, force = false) {
	if (this._state === state && force !== true) {
		return false;
	}
	this._state = state;

	this.isUpdating = true;
	this._setState(this.element, state);
	this.isUpdating = false;
	return true;
};

/**
 * Set the container position back or front.
 * @param {string} position    See ViewContainer.zPosition for options 
 * @returns {void}
 */
ViewContainer.prototype.setPosition = function (position) {
	if (this._zPosition === position) {
		return false;
	}
	this._zPosition = position;

	this.isUpdating = true;
	this._setPosition(this.element, position);
	this.isUpdating = false;
	return true;
};

/**
 * Call a custom defined function (if defined) when a property changes
 * @param {object} config
 * @returns {boolean}
 */
ViewContainer.prototype.setConfig = function (config = {}) {
	this.config = config;

	return true;
};

ViewContainer.prototype.update = function(config) {
	checkType(config, 'object', 'specs');

	this.setTitle(config.title);
	this.setMenu(config);
	this.setState(config.state);


	// Detect size changes by trigger
	let currentSize = this.getInnerSize();
	let newHeight;
	let newWidth;

	if (this.config && config.height !== this.config.height) {
		newHeight = config.height;
	}

	if (this.config && config.width !== this.config.width) {
		newWidth = config.width;
	}

	// if size changed by trigger apply changes
	if (! _.isNil(newWidth) || ! _.isNil(newHeight)) {
		this.setSize(newWidth || currentSize.width, newHeight || currentSize.height);
	}

	const zPosition = _.get(config, 'zPosition');
	const onBottom = isTrue(_.get(config, 'onBottom'));
	const onTop = isTrue(_.get(config, 'onTop'));

	if (zPosition === 'front') {
		this.moveToFront();
		this.updateState('zPosition', null);
	} else if (zPosition === 'back') {
		this.moveToBack();
		this.updateState('zPosition', null);
	} else if (onBottom) {
		this.moveToBack();
		this.updateState('onBottom', false);
	} else if (onTop) {
		this.moveToFront();
		this.updateState('onTop', false);
	}

	this.setConfig(config);
};

/**
 * Updates the `container` property of the view model.
 * @param {string} property        Property of `container` in the model (e.g. `id` or `state`).
 * @param value                    Value to set it to
 */
ViewContainer.prototype.updateState = function (property, value) {
	// 1. Prompts don't have renderers
	// 2. Container should not try to update model if container is applying updates from the model, to prevent infinite loops
	if (this._renderer && !this.isUpdating) {
		this._renderer.setContainerState(property, value);
	}
};

ViewContainer.prototype.isInDOM = function() {
	if (! _.isElement(_.get(this.$element, '0')))
		return false;

	return document.body.contains(this.$element[0]);
};


ViewContainer.prototype.moveToFront = function() {
	if (! this.element) return;
	this._moveToFront(this.element);
};



ViewContainer.prototype.moveToBack = function() {
	if (! this.element) return;
	this._moveToBack(this.element);
};



module.exports = ViewContainer;
